import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Lock, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { describeFailure } from '@/lib/describe-failure';
import { getPolicyTechSources, setPolicyTechSources } from '@/api/organization.api.private';
import { useOrganizationContext } from '../organization.context';

const DEV_TEAM_DISCLAIMER = 'Should only be changed by the dev team';
const EMPTY_HINT = 'Single-zip mode — add a source to split drops into sections.';
const KEY_LOCK_HINT = "The key cannot change — it identifies this zip's imported content.";
const RENAME_WARNING = 'Changing the name rebuilds the whole section on the next drop.';
const MATCH_WARNING =
  'Zips named for the old prefix will stop matching — their next drop will fail.';
const KEY_PATTERN = /^[a-z0-9-]+$/;

const toEntry = ({ key, match, siteName }) =>
  JSON.stringify([key.trim(), match.trim(), siteName.trim()]);

// Sets internalConfigurations.policyTech.sources. The list itself is the multi-zip
// switch: empty = single-zip mode. Keys are permanent identities — changing one after
// content exists duplicates that zip's content on the next drop, so saved keys are locked.
export default function PolicyTechSourcesSettings() {
  const { organization } = useOrganizationContext();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);
  const [persisted, setPersisted] = useState([]);
  const [rowToRemove, setRowToRemove] = useState(null);
  // 'first' (empty → non-empty) or 'last' (non-empty → empty) — both are mode
  // switches that risk duplicate re-imports, so both confirm before saving.
  const [modeSwitchConfirm, setModeSwitchConfirm] = useState(null);

  // Stable React keys for rows whose `key` field is still being typed.
  const nextRowIdRef = useRef(0);
  const makeRow = useCallback(source => {
    return {
      rowId: nextRowIdRef.current++,
      key: source?.key ?? '',
      match: source?.match ?? '',
      siteName: source?.siteName ?? '',
      isNew: !source,
      originalMatch: source?.match ?? null,
      originalSiteName: source?.siteName ?? null,
    };
  }, []);

  const load = useCallback(
    async (isCancelled = () => false) => {
      setLoading(true);
      setLoadError(null);

      try {
        const res = await getPolicyTechSources(organization.id);
        if (isCancelled()) return;

        if (res.error) {
          console.error('Failed to load PolicyTech sources', {
            organizationId: organization.id,
            code: res.code,
          });
          toast({
            title: 'Failed to load PolicyTech sources',
            description: describeFailure(res),
            variant: 'destructive',
          });
          // Save is a full replace — editing off a failed read would wipe the real config.
          setLoadError(describeFailure(res));
          return;
        }

        const sources = res.data?.sources ?? [];
        setPersisted(sources.map(({ key, match, siteName }) => ({ key, match, siteName })));
        setRows(sources.map(source => makeRow(source)));
      } catch (error) {
        if (isCancelled()) return;
        console.error('Failed to load PolicyTech sources', {
          organizationId: organization.id,
          error,
        });
        toast({
          title: 'Failed to load PolicyTech sources',
          description: 'An error occurred while loading the settings',
          variant: 'destructive',
        });
        setLoadError('Something went wrong loading the settings.');
      } finally {
        if (!isCancelled()) setLoading(false);
      }
    },
    [organization.id, toast, makeRow],
  );

  useEffect(() => {
    let cancelled = false;
    load(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [load]);

  const { isDirty, validationErrors } = useMemo(() => {
    // Sorted-array comparison rather than Sets so duplicate rows still read as dirty.
    const currentEntries = rows.map(toEntry).sort();
    const persistedEntries = persisted.map(toEntry).sort();
    const dirty =
      currentEntries.length !== persistedEntries.length ||
      currentEntries.some((entry, index) => entry !== persistedEntries[index]);

    const errors = [];
    const trimmed = rows.map(row => ({
      key: row.key.trim(),
      match: row.match.trim(),
      siteName: row.siteName.trim(),
    }));
    if (trimmed.some(row => !row.key || !row.match || !row.siteName)) {
      errors.push('Every source needs a key, a file-name prefix, and a section name.');
    }
    if (trimmed.some(row => row.key && !KEY_PATTERN.test(row.key))) {
      errors.push('Keys may only contain lowercase letters, numbers, and dashes.');
    }
    const keys = trimmed.map(row => row.key).filter(Boolean);
    if (new Set(keys).size !== keys.length) {
      errors.push('Keys must be unique.');
    }
    // Matches and section names compare case-insensitively, mirroring the server:
    // filename routing is case-insensitive, and section identity is name-based.
    const matches = trimmed.map(row => row.match.toLowerCase()).filter(Boolean);
    if (new Set(matches).size !== matches.length) {
      errors.push('File-name prefixes must be unique.');
    }
    const siteNames = trimmed.map(row => row.siteName.toLowerCase()).filter(Boolean);
    if (new Set(siteNames).size !== siteNames.length) {
      errors.push('Section names must be unique.');
    }
    // A prefix of another match would make zip-to-source routing order-dependent.
    // Strict prefixes only — equal matches are already the uniqueness error above.
    if (
      matches.some((match, i) =>
        matches.some((other, j) => i !== j && other !== match && other.startsWith(match)),
      )
    ) {
      errors.push('A file-name prefix must not be the start of another.');
    }
    return { isDirty: dirty, validationErrors: errors };
  }, [rows, persisted]);

  const updateRow = (rowId, field, value) => {
    setRows(prev => prev.map(row => (row.rowId === rowId ? { ...row, [field]: value } : row)));
  };

  const removeRow = rowId => setRows(prev => prev.filter(row => row.rowId !== rowId));

  const onRemoveClick = row => {
    // Unsaved rows have no imported content to orphan — no confirm needed.
    if (row.isNew) removeRow(row.rowId);
    else setRowToRemove(row);
  };

  const onAddSource = () => setRows(prev => [...prev, makeRow()]);

  const save = async () => {
    setSaving(true);

    try {
      const sources = rows.map(row => ({
        key: row.key.trim(),
        match: row.match.trim(),
        siteName: row.siteName.trim(),
      }));
      const res = await setPolicyTechSources({ id: organization.id, sources });

      if (res.error) {
        console.error('Failed to update PolicyTech sources', {
          organizationId: organization.id,
          code: res.code,
        });
        toast({
          title: 'Failed to update PolicyTech sources',
          description: describeFailure(res),
          variant: 'destructive',
        });
        return;
      }

      const saved = (res.data?.sources ?? sources).map(({ key, match, siteName }) => ({
        key,
        match,
        siteName,
      }));
      setPersisted(saved);
      setRows(saved.map(source => makeRow(source)));
      toast({
        status: 'success',
        title: 'PolicyTech sources updated',
      });
    } catch (error) {
      console.error('Failed to update PolicyTech sources', {
        organizationId: organization.id,
        error,
      });
      toast({
        title: 'Failed to update PolicyTech sources',
        description: 'An error occurred while saving the settings',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const onSaveClick = () => {
    // The client can't know whether synced content already exists, so it always
    // confirms mode switches (empty ⇄ non-empty) before persisting.
    if (persisted.length === 0 && rows.length > 0) return setModeSwitchConfirm('first');
    if (persisted.length > 0 && rows.length === 0) return setModeSwitchConfirm('last');
    save();
  };

  // Rendered as the "Zip sources" section of the combined PolicyTech card.
  return (
    <div className="space-y-4">
      <p className="text-xs font-medium text-amber-600">{DEV_TEAM_DISCLAIMER}</p>
      {loadError ? (
        <div className="space-y-2">
          <p className="text-sm text-destructive">
            Couldn&apos;t load the current sources: {loadError}
          </p>
          <p className="text-xs text-muted-foreground">
            Editing is disabled so the existing configuration isn&apos;t overwritten.
          </p>
          <Button variant="outline" onClick={() => load()}>
            Retry
          </Button>
        </div>
      ) : loading ? (
        <div className="text-xs text-muted-foreground">Loading sources...</div>
      ) : (
        <>
          {rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">{EMPTY_HINT}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Key</TableHead>
                  <TableHead>File name starts with</TableHead>
                  <TableHead>Section name</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(row => (
                  <TableRow key={row.rowId}>
                    <TableCell className="p-2 align-top">
                      {row.isNew ? (
                        <Input
                          value={row.key}
                          placeholder="e.g. anesthesia"
                          aria-label="Key"
                          disabled={saving}
                          onChange={event => updateRow(row.rowId, 'key', event.target.value)}
                        />
                      ) : (
                        <span
                          className="flex h-10 items-center gap-1.5 text-sm"
                          title={KEY_LOCK_HINT}>
                          <Lock className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
                          {row.key}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="p-2 align-top">
                      <div className="space-y-1">
                        <Input
                          value={row.match}
                          placeholder="e.g. ANES_"
                          aria-label={`File name starts with — ${row.key || 'new source'}`}
                          disabled={saving}
                          onChange={event => updateRow(row.rowId, 'match', event.target.value)}
                        />
                        {!row.isNew && row.match !== row.originalMatch && (
                          <p className="text-xs text-amber-600">{MATCH_WARNING}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="p-2 align-top">
                      <div className="space-y-1">
                        <Input
                          value={row.siteName}
                          placeholder="e.g. Anesthesia"
                          aria-label={`Section name — ${row.key || 'new source'}`}
                          disabled={saving}
                          onChange={event => updateRow(row.rowId, 'siteName', event.target.value)}
                        />
                        {!row.isNew && row.siteName !== row.originalSiteName && (
                          <p className="text-xs text-amber-600">{RENAME_WARNING}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="p-2 align-top">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="group"
                        aria-label={`Remove source ${row.key || '(new)'}`}
                        disabled={saving}
                        onClick={() => onRemoveClick(row)}>
                        <Trash2 className="h-4 w-4 text-muted-foreground group-hover:text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {isDirty && validationErrors.length > 0 && (
            <div className="space-y-1">
              {validationErrors.map(error => (
                <p key={error} className="text-xs text-destructive">
                  {error}
                </p>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2">
            <Button variant="outline" disabled={saving} onClick={onAddSource}>
              Add source
            </Button>
            <Button
              onClick={onSaveClick}
              loading={saving}
              disabled={saving || !isDirty || validationErrors.length > 0}>
              Save
            </Button>
          </div>
        </>
      )}

      <Dialog
        open={!!rowToRemove}
        onOpenChange={open => {
          if (!open) setRowToRemove(null);
        }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove source</DialogTitle>
            <DialogDescription>
              {rows.length === 1
                ? 'This is the last source — removing it returns the org to single-zip mode. Its imported content is not deleted, but the next drop will import as a single zip and duplicate content that was split into sections. Continue?'
                : 'Removing a source does not delete its imported content, but the next drop of this zip will fail until a source matches it again. Continue?'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button
              variant="destructive"
              onClick={() => {
                removeRow(rowToRemove.rowId);
                setRowToRemove(null);
              }}>
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!modeSwitchConfirm}
        onOpenChange={open => {
          if (!open) setModeSwitchConfirm(null);
        }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {modeSwitchConfirm === 'last' ? 'Remove all sources?' : 'Add the first source?'}
            </DialogTitle>
            <DialogDescription>
              {modeSwitchConfirm === 'last'
                ? 'Removing all sources returns the org to single-zip mode. If sectioned content was already synced, the next drop will re-import everything as duplicates. Clean the org first (see the runbook). Continue?'
                : 'If this org already has synced PolicyTech content, adding sources will make the next drop re-import everything as duplicates. Clean the org first (see the runbook). Continue?'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button
              onClick={() => {
                setModeSwitchConfirm(null);
                save();
              }}>
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
